import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { money } from './rateEngine';
import { EmptyState, Kpi, PeriodPicker, StatusBadge } from './shared';

const newAdjustment = () => ({ id: `ADJ-${Date.now()}`, label: '', amount: '' });

/** Adjustments (bonuses +, deductions −) are editable only while the settlement is a draft. */
const AdjustmentsEditor = ({ settlement, onSave }) => {
  const [rows, setRows] = useState(settlement.adjustments);
  const [saving, setSaving] = useState(false);
  useEffect(() => setRows(settlement.adjustments), [settlement.adjustments]);

  const editable = settlement.status === 'draft';
  const dirty = JSON.stringify(rows) !== JSON.stringify(settlement.adjustments);
  const update = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      await onSave(settlement, rows.filter((r) => r.label.trim() && Number(r.amount)).map((r) => ({ ...r, amount: Number(r.amount) })));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">Adjustments</p>
      {rows.length === 0 && <p className="text-sm text-muted-foreground">None.</p>}
      {rows.map((r) => (
        <div key={r.id} className="flex gap-2 items-center">
          <Input
            id={`settlement-adj-label-${r.id}`}
            value={r.label}
            disabled={!editable}
            placeholder="e.g. Fuel advance, Damage chargeback, Bonus"
            onChange={(e) => update(r.id, { label: e.target.value })}
          />
          <Input
            id={`settlement-adj-amount-${r.id}`}
            type="number"
            step="0.01"
            value={r.amount}
            disabled={!editable}
            placeholder="± amount"
            className="w-[140px] text-right"
            onChange={(e) => update(r.id, { amount: e.target.value })}
          />
          {editable && (
            <Button size="sm" variant="ghost" onClick={() => setRows((prev) => prev.filter((x) => x.id !== r.id))}>
              <i className="fas fa-times"></i>
            </Button>
          )}
        </div>
      ))}
      {editable && (
        <div className="flex gap-2">
          <Button id={`settlement-adj-add-${settlement.driver.id}`} size="sm" variant="outline" onClick={() => setRows((prev) => [...prev, newAdjustment()])}>
            <i className="fas fa-plus mr-2"></i>Add adjustment
          </Button>
          {dirty && (
            <Button id={`settlement-adj-save-${settlement.driver.id}`} size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save adjustments'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Screen 2 — Driver Settlements
 * Bi-weekly GROSS pay per driver from POSTED manifests (driver's own rate card) plus adjustments; vacation pay is
 * accrued for employees. The accountant finalizes payroll (deductions, net pay) outside the TMS.
 * Back-dated manifests whose own period is already approved roll into the next open period.
 * Workflow: draft → approved → processed (by the accountant).
 */
const DriverSettlements = ({ period, settlements, onSaveAdjustments, onApprove, onMarkProcessed, onOpenManifest }) => {
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const act = async (s, fn) => {
    setBusyId(s.id);
    try {
      await fn(s);
    } finally {
      setBusyId(null);
    }
  };

  const sum = (k) => settlements.reduce((s, x) => s + x[k], 0);

  return (
    <div className="space-y-6" id="driver-settlements-screen">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">Driver Settlements</h2>
          <p className="text-sm text-muted-foreground">
            Bi-weekly gross pay from posted manifests, for the accountant to finalize. Unposted manifests are excluded until they're posted.
          </p>
        </div>
        <PeriodPicker {...period} idPrefix="settlements" title="Pay period" />
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi label="Drivers" value={settlements.length} sub={`${settlements.filter((s) => s.status === 'processed').length} processed by accountant`} icon="fa-users" />
        <Kpi label="Manifest Pay" value={money(sum('gross'))} sub={`${sum('pieces')} pieces`} icon="fa-wallet" />
        <Kpi label="Gross Pay" value={money(sum('grossPay'))} sub={`incl. ${money(sum('adjTotal'))} adjustments`} icon="fa-money-check-alt" accent="text-emerald-500" />
        <Kpi label="Vacation Accrued" value={money(sum('vacationAccrued'))} sub="employees only — not paid out" icon="fa-umbrella-beach" />
      </div>

      {settlements.length === 0 ? (
        <EmptyState icon="fa-wallet" title="No settlements this pay period">
          Settlements appear once manifests with an assigned driver exist for the period.
        </EmptyState>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-muted/80 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Driver</th>
                  <th className="text-right p-3">Manifests</th>
                  <th className="text-right p-3">Pieces</th>
                  <th className="text-right p-3">Manifest pay</th>
                  <th className="text-right p-3">Adj.</th>
                  <th className="text-right p-3">Gross pay</th>
                  <th className="text-right p-3">Vacation accrued</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {settlements.map((s) => {
                  const open = openId === s.id;
                  const busy = busyId === s.id;
                  return (
                    <React.Fragment key={s.id}>
                      <tr className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => setOpenId(open ? null : s.id)} id={`settlement-row-${s.driver.id}`}>
                        <td className="p-3">
                          <i className={`fas fa-chevron-${open ? 'down' : 'right'} text-[10px] mr-2 text-muted-foreground`}></i>
                          <span className="font-medium">{s.driver.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{s.driver.id}</span>
                          {s.employmentType === 'owner_operator' && <span className="ml-2 text-[10px] uppercase text-sky-500">owner-operator</span>}
                          {s.unpostedCount > 0 && (
                            <span className="text-xs text-amber-500 ml-2">
                              <i className="fas fa-exclamation-triangle mr-1"></i>
                              {s.unpostedCount} unposted
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right">{s.lines.length}</td>
                        <td className="p-3 text-right">{s.pieces}</td>
                        <td className="p-3 text-right">{money(s.gross)}</td>
                        <td className={`p-3 text-right ${s.adjTotal < 0 ? 'text-red-500' : ''}`}>{money(s.adjTotal)}</td>
                        <td className="p-3 text-right font-semibold">{money(s.grossPay)}</td>
                        <td className="p-3 text-right text-muted-foreground">
                          {s.vacationRate ? `${money(s.vacationAccrued)} (${+(s.vacationRate * 100).toFixed(2)}%)` : '—'}
                        </td>
                        <td className="p-3">
                          <StatusBadge status={s.status} />
                        </td>
                        <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                          {s.status === 'draft' && (
                            <Button
                              id={`settlement-approve-${s.driver.id}`}
                              size="sm"
                              disabled={busy || s.unpostedCount > 0 || s.lines.length === 0}
                              title={s.unpostedCount > 0 ? 'Post all of this driver\'s manifests first' : undefined}
                              onClick={() => act(s, onApprove)}
                            >
                              Approve
                            </Button>
                          )}
                          {s.status === 'approved' && (
                            <Button
                              id={`settlement-processed-${s.driver.id}`}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              disabled={busy}
                              title="The accountant has finalized payroll for this driver"
                              onClick={() => act(s, onMarkProcessed)}
                            >
                              Mark Processed
                            </Button>
                          )}
                          {s.status === 'processed' && <span className="text-xs text-muted-foreground">{s.processedAt ? new Date(s.processedAt).toLocaleDateString('en-CA') : ''}</span>}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-muted/20">
                          <td colSpan={9} className="p-4">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <p className="text-xs uppercase tracking-wide text-muted-foreground pb-2">Posted manifests</p>
                                {s.lines.length === 0 && <p className="text-sm text-muted-foreground">None posted yet.</p>}
                                {s.lines.map((l) => (
                                  <button
                                    key={l.manifestId}
                                    onClick={() => onOpenManifest(l.manifestId)}
                                    className="w-full flex justify-between text-sm py-1.5 border-b border-border/60 hover:text-emerald-500"
                                  >
                                    <span>
                                      {l.date} · <span className="font-mono">{l.route}</span>
                                      {l.backdated && <span className="ml-2 text-[10px] text-sky-500 uppercase">back-dated</span>}
                                    </span>
                                    <span>
                                      {l.pieces} pcs · {money(l.amount)}
                                    </span>
                                  </button>
                                ))}
                              </div>
                              <AdjustmentsEditor settlement={s} onSave={onSaveAdjustments} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DriverSettlements;
